import { ApolloError, useLazyQuery } from "@apollo/client";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Theme,
  Typography
} from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTime as d } from "luxon";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { GET_VEHICLE_AVAILABILITY } from "../../../../../graphql/bookings/getTestDriveVehicleAvailability";
import { BOOKING_CATEGORY, IAddressLocation, ITestDriveBookingDetails } from "../../../../../reducers/bookings/types";
import { IAppState } from "../../../../../store";
import { useSnackBar } from "../../../../common/SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../../../common/SnackbarWrapper/SnackbarWrapper";
import { formatGraphQLErrorMessage } from "../../../../common/utils";
import { TestDriveExperiences, TestDriveTypes } from "../../utils";

interface IVehicleAvailability {
  timeSlot: string;
  slotId: string;
  driverId: string;
}

interface IVehicleSlotSelection {
  testDriveType: TestDriveTypes;
  testDriveExperience: TestDriveExperiences;
  selectedSlot: string;
  selectedDate: string;
  vehicleId: string;
  calendarInitialSlot: number;
  extendedTestDriveDuration: number;
  driverId: string;
  slotId: string;
}

interface IProps {
  vehicleId: string;
  pickupDate: string;
  dropoffDateTime: string;
  pickupServiceLocation: string;
  testDriveBookingDetails?: ITestDriveBookingDetails;
  pickupOtherLocation?: IAddressLocation;
  setPickupDateTime?: (
    timeSlot: string,
    duration: number,
    driverId: string,
    slotId: string,
  ) => void;
  setDropOffDateTime?: (timeSlot: string, driverId: string, slotId: string) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    btnContainer: {
      minWidth: "120px"
    },
    timeSlot: {
      marginBottom: theme.spacing(0.5)
    },
    selected: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      "&:hover": {
        backgroundColor: theme.palette.primary.dark
      }
    },
    slotsContainer: {
      minWidth: "400px"
    }
  })
);

const initialVehicleSelection: IVehicleSlotSelection = {
  testDriveType: TestDriveTypes.STANDARD,
  testDriveExperience: TestDriveExperiences.CONSULTATION,
  selectedSlot: "",
  vehicleId: "",
  selectedDate: d
    .now()
    .toUTC()
    .toISO(),
  calendarInitialSlot: 0,
  extendedTestDriveDuration: 1,
  driverId: "",
  slotId: ""
};

export const TestDriveSlotSelector: React.FC<IProps> = ({
  pickupDate,
  vehicleId,
  dropoffDateTime,
  pickupServiceLocation,
  testDriveBookingDetails,
  pickupOtherLocation,
  setPickupDateTime,
  setDropOffDateTime
}) => {
  const snackbar = useSnackBar();
  const classes = useStyles();

  const [selectedDropOffSlot, setSelectedDropOffSlot] = useState<number>(
    initialVehicleSelection.calendarInitialSlot
  );
  const [selectedPickUpSlot, setSelectedPickupSlot] = useState<number>(
    initialVehicleSelection.calendarInitialSlot
  );
  const [vehicleAvailabilityDropOffSlots, setVehicleAvailabilityDropOffSlots] = useState<
    IVehicleAvailability[]
  >([]);
  const [vehicleAvailabilityPickUpSlots, setVehicleAvailabilityPickUpSlots] = useState<
    IVehicleAvailability[]
  >([]);
  const [isDriverAvailableForDropOff, setIsDriverAvailableForDropOff] = useState<boolean>(false)
  const [isDriverAvailableForPickup, setIsDriverAvailableForPickUp] = useState<boolean>(false)

  const userState = useSelector((state: IAppState) => state.userReducer);
  const branch = userState.currentBranch
  console.log(pickupOtherLocation)
  const [
    getTestDriveAvailability,
    { loading: availabilityLoading }
  ] = useLazyQuery(GET_VEHICLE_AVAILABILITY, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data.getTestDriveVehicleAvailability) {
        const { isExtended, dropoffSlots, pickupSlots } = data.getTestDriveVehicleAvailability;
        if (isExtended && (dropoffSlots?.timeSlots.length || pickupSlots?.timeSlots.length)) {
          setIsDriverAvailableForDropOff(!!dropoffSlots?.driverSlotsExist);
          setIsDriverAvailableForPickUp(!!pickupSlots?.driverSlotsExist);

          setVehicleAvailabilityDropOffSlots(dropoffSlots?.timeSlots || []);
          setVehicleAvailabilityPickUpSlots(pickupSlots?.timeSlots || []);

          if (setPickupDateTime && setDropOffDateTime) {
            setPickupDateTime(dropoffSlots?.timeSlots[selectedDropOffSlot].timeSlot, testDriveBookingDetails?.extendedTestDriveHours || 1, dropoffSlots?.timeSlots[selectedDropOffSlot].driverId, dropoffSlots?.timeSlots[selectedDropOffSlot].slotId)
            setDropOffDateTime(pickupSlots?.timeSlots[selectedPickUpSlot].timeSlot, pickupSlots?.timeSlots[selectedPickUpSlot].driverId, pickupSlots?.timeSlots[selectedPickUpSlot].slotId)
          }
        } else {
          if (setPickupDateTime) {
            setPickupDateTime(dropoffSlots?.timeSlots[selectedDropOffSlot].timeSlot, testDriveBookingDetails?.extendedTestDriveHours || 1, dropoffSlots?.timeSlots[selectedDropOffSlot].driverId, dropoffSlots?.timeSlots[selectedDropOffSlot].slotId)
          }
          setIsDriverAvailableForDropOff(!!dropoffSlots?.driverSlotsExist && dropoffSlots?.timeSlots?.length);
          setVehicleAvailabilityDropOffSlots(dropoffSlots?.timeSlots || []);
        }
      }
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    }
  });

  useEffect(() => {
    if (vehicleId && pickupDate) {
      let extendedTestDriveDuration = 0;
      if (
        testDriveBookingDetails?.type === TestDriveTypes.EXTENDED &&
        testDriveBookingDetails.extendedTestDriveHours
      ) {
        extendedTestDriveDuration =
          testDriveBookingDetails.extendedTestDriveHours;
      }
      getTestDriveAvailability({
        variables: {
          availabilityParams: {
            dropoffQueryDate: dropoffDateTime,
            pickupQueryDate: pickupDate,
            vehicleIds: [vehicleId],
            branchId: branch.id,
            category: BOOKING_CATEGORY.TEST_DRIVE,
            pickupServiceLocation: pickupServiceLocation,
            dropoffOtherLocation: {
              street: pickupOtherLocation?.street,
              city: pickupOtherLocation?.city,
              fullAddress: pickupOtherLocation?.fullAddress,
              state: pickupOtherLocation?.state,
              country: pickupOtherLocation?.country,
              zipcode: pickupOtherLocation?.zipcode,
              geometry: {
                type: pickupOtherLocation?.location.type,
                latitude: pickupOtherLocation?.location.latitude,
                longitude: pickupOtherLocation?.location.longitude
              }
            },
            extendedTestDriveDuration: testDriveBookingDetails?.extendedTestDriveHours
          }
        }
      });
    }
  }, [vehicleId]);

  const handleDropOffSlotSelection = (
    slot: IVehicleAvailability,
    selectedSlotIndex: number
  ) => {
    if (setPickupDateTime) {
      setPickupDateTime(slot.timeSlot, testDriveBookingDetails?.extendedTestDriveHours || 1, slot.driverId, slot.slotId);
    }
    setSelectedDropOffSlot(selectedSlotIndex);
  };

  const handlePickUpSlotSelection = (
    slot: IVehicleAvailability,
    selectedSlotIndex: number
  ) => {
    if (setDropOffDateTime) {
      setDropOffDateTime(slot.timeSlot, slot.driverId, slot.slotId);
    }
    setSelectedPickupSlot(selectedSlotIndex);
  };

  return (
    <Grid
      container
      alignContent="center"
      justifyContent="space-between"
      alignItems="center"
    >
      <Grid container className={classes.slotsContainer} spacing={2}>
        {availabilityLoading && (
          <Grid container item xs={12} spacing={1}>
            <CircularProgress />
          </Grid>
        )}

        {!availabilityLoading && vehicleAvailabilityDropOffSlots && (
          <Grid item xs={6}>
            <Grid item xs={12}><Typography variant={"subtitle1"}>Select Drop-off Slot</Typography></Grid>
            <Grid item xs={12}><Box mt={1}></Box></Grid>
            <Grid container spacing={2}>
              {vehicleAvailabilityDropOffSlots.map((slot, index) => (
                <Grid
                  item
                  xs={12}
                  sm={2}
                  key={index}
                  className={classes.btnContainer}
                >
                  <Button
                    variant="outlined"
                    fullWidth
                    className={`${classes.timeSlot} ${selectedDropOffSlot === index ? classes.selected : ""
                      }`}
                    onClick={() => handleDropOffSlotSelection(slot, index)}
                  >
                    {slot.timeSlot}
                  </Button>
                </Grid>
              ))}
              {
                !isDriverAvailableForDropOff && (
                  <Grid item xs={12}><Typography><strong>Note:</strong> Driver is not available please contact to rental operator.</Typography></Grid>
                )
              }
            </Grid>
          </Grid>
        )}
        {!availabilityLoading && vehicleAvailabilityPickUpSlots.length && (
          <Grid item xs={6}>
            <Grid item xs={12}><Typography variant={"subtitle1"}>Select pick-up Slot</Typography></Grid>
            <Grid item xs={12}><Box mt={1}></Box></Grid>
            <Grid container spacing={2}>
              {vehicleAvailabilityPickUpSlots.map((slot, index) => (
                <Grid
                  item
                  xs={12}
                  sm={2}
                  key={index}
                  className={classes.btnContainer}
                >
                  <Button
                    variant="outlined"
                    fullWidth
                    className={`${classes.timeSlot} ${selectedPickUpSlot === index ? classes.selected : ""
                      }`}
                    onClick={() => handlePickUpSlotSelection(slot, index)}
                  >
                    {slot.timeSlot}
                  </Button>
                </Grid>
              ))}
              {
                !isDriverAvailableForPickup && (
                  <Grid item xs={12}><Typography><strong>Note:</strong> Driver is not available please contact to rental operator.</Typography></Grid>
                )
              }
            </Grid>
          </Grid>
        )}
        {!availabilityLoading && !vehicleAvailabilityDropOffSlots.length && (
          <Grid container item xs={12} spacing={1}>
            <Grid item xs={12}>
              <Typography variant="body1" style={{ marginTop: "16px" }}>
                Vehicle Not Available for Selected Date.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1" style={{ marginTop: "16px" }}>
                Please Select a Different Date.
              </Typography>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
